import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { authFetch, getStoredAccount } from "../utils/auth";

type IssueStatus = "backlog" | "progress" | "testing" | "done";
type IssuePriority = "critical" | "high" | "medium" | "low";

type IssueAttachment = {
  id: string;
  name: string;
  size: string;
  type: string;
  previewUrl?: string;
  url?: string;
  file?: File;
};

type Issue = {
  id: string;
  backendId?: number;
  title: string;
  client: string;
  status: IssueStatus;
  priority: IssuePriority;
  assignee: string;
  dueDate: string;
  tags: string[];
  description: string;
  attachments: IssueAttachment[];
};

type Column = {
  id: IssueStatus;
  title: string;
  description: string;
};

type ApiClient = {
  id: number;
  name: string;
  slug: string;
};

type ApiIssueAttachment = {
  id: number;
  original_name: string;
  content_type: string;
  size: number;
  url: string;
  is_image: boolean;
};

type ApiIssue = {
  id: number;
  publicId: string;
  title: string;
  client: ApiClient;
  status: IssueStatus;
  priority: IssuePriority;
  assignee: string;
  dueDate: string | null;
  description: string;
  tags: string[];
  attachments: ApiIssueAttachment[];
  sortOrder: number;
};

type PaginatedResponse<T> = {
  results: T[];
};

const columns: Column[] = [
  { id: "backlog", title: "대기", description: "접수 후 검토가 필요한 요청" },
  {
    id: "progress",
    title: "진행 중",
    description: "개발 또는 분석이 진행 중인 이슈",
  },
  {
    id: "testing",
    title: "테스트",
    description: "검수와 QA 확인이 필요한 항목",
  },
  { id: "done", title: "완료", description: "배포 또는 전달이 완료된 작업" },
];

const initialIssues: Issue[] = [
  {
    id: "NOS-1024",
    title: "LLM 비용 리포트 CSV Export 추가",
    client: "고객사",
    status: "backlog",
    priority: "high",
    assignee: "Yuseong",
    dueDate: "2026-05-08",
    tags: ["Billing", "CSV", "Admin"],
    description:
      "## 요구사항\n월별 LLM 사용량 리포트를 CSV로 다운로드할 수 있어야 합니다.\n\n- 모델별 토큰 합계\n- 일자별 비용\n- 프로젝트별 비용 분리",
    attachments: [
      {
        id: "att-1",
        name: "billing-sample.png",
        size: "820KB",
        type: "image/png",
      },
    ],
  },
  {
    id: "NOS-1025",
    title: "Vector DB 지연시간 알림 정책 조정",
    client: "고객사",
    status: "backlog",
    priority: "medium",
    assignee: "Infra",
    dueDate: "2026-05-10",
    tags: ["Infra", "Alert"],
    description:
      "## 배경\n일시적 지연에도 경고가 자주 발생하고 있습니다.\n\n### 목표\n5분 평균 latency 기준으로 알림을 발송하도록 조정합니다.",
    attachments: [],
  },
  {
    id: "NOS-1031",
    title: "문서 라이브러리 검색 UX 개선",
    client: "고객사",
    status: "progress",
    priority: "medium",
    assignee: "Frontend",
    dueDate: "2026-05-06",
    tags: ["Docs", "Search", "UX"],
    description:
      "## 작업 내용\n문서 제목, 태그, 본문 일부를 대상으로 빠르게 검색할 수 있는 UI가 필요합니다.\n\n`/admin/docs` 내 사이드바 상단에 배치합니다.",
    attachments: [],
  },
  {
    id: "NOS-1033",
    title: "Webhook 실패 재시도 로그 노출",
    client: "고객사",
    status: "progress",
    priority: "critical",
    assignee: "Backend",
    dueDate: "2026-05-04",
    tags: ["Webhook", "Logs"],
    description:
      "## 이슈\nWebhook 전송 실패 시 고객사가 원인을 확인하기 어렵습니다.\n\n- 실패 사유\n- 응답 코드\n- 재시도 횟수\n- 마지막 재시도 시간",
    attachments: [
      {
        id: "att-2",
        name: "webhook-error-response.jpg",
        size: "1.4MB",
        type: "image/jpeg",
      },
    ],
  },
  {
    id: "NOS-1035",
    title: "모바일 하단 탭 접근성 QA",
    client: "고객사",
    status: "testing",
    priority: "low",
    assignee: "QA",
    dueDate: "2026-05-09",
    tags: ["Mobile", "A11y"],
    description:
      "## QA 체크리스트\n- 탭 순서 확인\n- 터치 타겟 크기 확인\n- active 상태 대비 확인\n- 스크린 리더 라벨 확인",
    attachments: [],
  },
  {
    id: "NOS-1036",
    title: "AI 에이전트 초기 프롬프트 템플릿 정리",
    client: "고객사",
    status: "testing",
    priority: "high",
    assignee: "AI Ops",
    dueDate: "2026-05-05",
    tags: ["Agent", "Prompt"],
    description:
      "## 목표\n고객사 운영 맥락에 맞는 기본 프롬프트 템플릿을 정리합니다.\n\n> 응답 톤은 간결하고 실행 가능한 형태를 우선합니다.",
    attachments: [],
  },
  {
    id: "NOS-1019",
    title: "관리자 로그인 Mock 인증 플로우 구성",
    client: "고객사",
    status: "done",
    priority: "medium",
    assignee: "Frontend",
    dueDate: "2026-05-02",
    tags: ["Auth", "Routing"],
    description:
      "## 완료 내용\n`/login`에서 Mock 인증 후 `/admin/dashboard`로 이동하도록 구현했습니다.",
    attachments: [],
  },
  {
    id: "NOS-1020",
    title: "AppShell 반응형 LNB / Tab Bar 구성",
    client: "고객사",
    status: "done",
    priority: "high",
    assignee: "Frontend",
    dueDate: "2026-05-02",
    tags: ["Layout", "Responsive"],
    description:
      "## 완료 내용\nDesktop, Tablet, Mobile 레이아웃을 분리하고 `Outlet` 기반 Admin Route를 연결했습니다.",
    attachments: [],
  },
];

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: clamp(20px, 3vw, 30px);
`;

const PageHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 20px;
  align-items: end;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.05;
  letter-spacing: -0.045em;
`;

const Description = styled.p`
  max-width: 760px;
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: clamp(14px, 2vw, 16px);
  line-height: 1.75;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 767px) {
    justify-content: stretch;
  }
`;

const ActionButton = styled.button`
  height: 44px;
  padding: 0 16px;
  border-radius: 14px;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 900;
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.2);

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(12px, 2vw, 16px);

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.article`
  padding: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
`;

const SummaryLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  font-weight: 900;
`;

const SummaryValue = styled.strong`
  display: block;
  margin-top: 10px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1;
  letter-spacing: -0.04em;
`;

const ApiNotice = styled.div<{ $tone?: "info" | "danger" }>`
  padding: 14px 16px;
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === "danger" ? "rgba(239, 68, 68, 0.35)" : theme.colors.border};
  border-radius: 18px;
  color: ${({ theme, $tone }) =>
    $tone === "danger" ? theme.colors.danger : theme.colors.textMuted};
  background: ${({ theme, $tone }) =>
    $tone === "danger" ? "rgba(239, 68, 68, 0.08)" : theme.colors.surface};
  font-size: 13px;
  font-weight: 800;
  line-height: 1.6;
`;

const Board = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(320px, 1fr);
  gap: clamp(16px, 2vw, 20px);
  overflow-x: auto;
  padding-bottom: 12px;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.borderStrong};
  }

  @media (min-width: 1320px) {
    grid-auto-columns: minmax(0, 1fr);
  }
`;

const ColumnPanel = styled.section`
  min-height: 620px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 26px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
  scroll-snap-align: start;
`;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding: 4px 4px 14px;
`;

const ColumnTitleGroup = styled.div`
  min-width: 0;
`;

const ColumnTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 17px;
  letter-spacing: -0.025em;
`;

const ColumnDescription = styled.p`
  margin-top: 5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  line-height: 1.5;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 12px;
  font-weight: 900;
`;

const CardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IssueCard = styled.button`
  width: 100%;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px;
  color: inherit;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  text-align: left;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.1);
  }
`;

const IssueMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const IssueId = styled.span`
  color: ${({ theme }) => theme.colors.textSoft};
  font-size: 12px;
  font-weight: 900;
`;

const PriorityBadge = styled.span<{ $priority: IssuePriority }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  color: ${({ theme, $priority }) =>
    $priority === "critical"
      ? theme.colors.danger
      : $priority === "high"
        ? theme.colors.warning
        : $priority === "medium"
          ? theme.colors.primary
          : theme.colors.success};
  background: ${({ $priority }) =>
    $priority === "critical"
      ? "rgba(239, 68, 68, 0.12)"
      : $priority === "high"
        ? "rgba(245, 158, 11, 0.14)"
        : $priority === "medium"
          ? "rgba(37, 99, 235, 0.12)"
          : "rgba(34, 197, 94, 0.12)"};
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
`;

const IssueTitle = styled.strong`
  display: block;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  line-height: 1.45;
  letter-spacing: -0.02em;
`;

const IssueInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 14px;
`;

const InfoItem = styled.div`
  min-width: 0;

  span {
    display: block;
    color: ${({ theme }) => theme.colors.textSoft};
    font-size: 11px;
    font-weight: 900;
  }

  strong {
    display: block;
    margin-top: 3px;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 11px;
  font-weight: 800;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: clamp(16px, 4vw, 40px);
  background: rgba(15, 23, 42, 0.52);
  backdrop-filter: blur(10px);
`;

const ModalPanel = styled.section`
  width: min(1120px, 100%);
  max-height: min(860px, 92vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 30px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.28);
`;

const ModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: clamp(20px, 3vw, 28px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalTitleGroup = styled.div`
  min-width: 0;
`;

const ModalKicker = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 900;
`;

const ModalTitle = styled.h2`
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(22px, 3vw, 32px);
  line-height: 1.18;
  letter-spacing: -0.04em;
`;

const CloseButton = styled.button`
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 22px;
  font-weight: 700;
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 0;
  min-height: 0;
  overflow: auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const EditorSection = styled.div`
  min-width: 0;
  padding: clamp(20px, 3vw, 28px);
`;

const SidePanel = styled.aside`
  padding: clamp(20px, 3vw, 28px);
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceMuted};

  @media (max-width: 900px) {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    border-left: 0;
  }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 900;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 900;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const TextInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 14px;
  font-weight: 700;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primarySoft};
  }
`;

const SelectInput = styled.select`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 14px;
  font-weight: 800;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primarySoft};
  }
`;

const RichEditor = styled.div`
  margin-top: 10px;
`;

const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 0;
  border-radius: 18px 18px 0 0;
  background: ${({ theme }) => theme.colors.surface};
`;

const ToolbarButton = styled.button`
  min-width: 38px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 12px;
  font-weight: 900;

  svg {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
    fill: currentColor;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EditorSurface = styled.div`
  min-height: 360px;
  padding: 16px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0 0 18px 18px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 14px;
  line-height: 1.75;
  outline: none;
  cursor: text;
  user-select: text;
  -webkit-user-select: text;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primarySoft};
  }

  h2,
  h3 {
    margin: 0 0 10px;
    color: ${({ theme }) => theme.colors.text};
  }

  ul,
  ol {
    margin: 8px 0 14px 18px;
  }

  blockquote {
    margin: 10px 0;
    padding: 10px 12px;
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.surface};
  }

  code {
    padding: 2px 6px;
    border-radius: 7px;
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
    font-family: "SFMono-Regular", Consolas, monospace;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
  }

  pre {
    margin: 10px 0;
    padding: 14px;
    overflow: auto;
    border-radius: 14px;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surface};
    font-family: "SFMono-Regular", Consolas, monospace;
  }

  &:empty::before {
    content: "이슈 내용을 입력하세요. 상단 버튼으로 제목, 목록, 인용 등을 쉽게 추가할 수 있습니다.";
    color: ${({ theme }) => theme.colors.textSoft};
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const DetailItem = styled.div`
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.surface};

  span {
    display: block;
    color: ${({ theme }) => theme.colors.textSoft};
    font-size: 11px;
    font-weight: 900;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 13px;
  }
`;

const DropZone = styled.label<{ $dragging: boolean }>`
  display: grid;
  place-items: center;
  min-height: 160px;
  margin-top: 18px;
  padding: 18px;
  border: 1.5px dashed
    ${({ theme, $dragging }) =>
      $dragging ? theme.colors.primary : theme.colors.borderStrong};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme, $dragging }) =>
    $dragging ? theme.colors.primarySoft : theme.colors.surface};
  text-align: center;
  cursor: pointer;

  input {
    display: none;
  }

  strong {
    display: block;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
  }

  span {
    display: block;
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.6;
  }
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
`;

const AttachmentItem = styled.button<{ $clickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 12px;
  border: 0;
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12px;
  font-weight: 800;
  text-align: left;
  cursor: ${({ $clickable }) => ($clickable ? "zoom-in" : "default")};

  &:hover {
    outline: ${({ theme, $clickable }) =>
      $clickable ? `1px solid ${theme.colors.primary}` : "none"};
  }

  strong {
    min-width: 0;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ImageViewerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 130;
  display: grid;
  place-items: center;
  padding: clamp(16px, 4vw, 40px);
  background: rgba(2, 6, 23, 0.78);
  backdrop-filter: blur(14px);
`;

const ImageViewerPanel = styled.section`
  width: min(1080px, 100%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 28px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.4);
`;

const ImageViewerHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  strong {
    min-width: 0;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ImageViewerBody = styled.div`
  min-height: 0;
  padding: clamp(14px, 3vw, 24px);
  overflow: auto;
  background:
    linear-gradient(45deg, rgba(148, 163, 184, 0.12) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(148, 163, 184, 0.12) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(148, 163, 184, 0.12) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(148, 163, 184, 0.12) 75%);
  background-position:
    0 0,
    0 12px,
    12px -12px,
    -12px 0;
  background-size: 24px 24px;

  img {
    display: block;
    max-width: 100%;
    max-height: 72vh;
    margin: 0 auto;
    border-radius: 18px;
    object-fit: contain;
  }
`;

const ModalFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 18px clamp(20px, 3vw, 28px);
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const SecondaryButton = styled.button`
  height: 42px;
  padding: 0 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  font-weight: 900;
`;

const PrimaryButton = styled.button`
  height: 42px;
  padding: 0 16px;
  border-radius: 14px;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 900;
`;

const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api").replace(
  /\/$/,
  "",
);

const requestJson = async <T,>(url: string, options?: RequestInit): Promise<T> => {
  const requestPath = url.startsWith(apiBaseUrl) ? url.replace(apiBaseUrl, "") : url;
  const response = await authFetch(requestPath, options);

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
};

const mapApiAttachment = (attachment: ApiIssueAttachment): IssueAttachment => ({
  id: String(attachment.id),
  name: attachment.original_name,
  size: formatFileSize(attachment.size),
  type: attachment.content_type,
  url: attachment.url,
  previewUrl: attachment.is_image ? attachment.url : undefined,
});

const mapApiIssue = (issue: ApiIssue): Issue => ({
  id: issue.publicId,
  backendId: issue.id,
  title: issue.title,
  client: issue.client.name,
  status: issue.status,
  priority: issue.priority,
  assignee: issue.assignee,
  dueDate: issue.dueDate ?? "",
  tags: issue.tags,
  description: issue.description,
  attachments: issue.attachments.map(mapApiAttachment),
});

const getIssuePayload = (issue: Issue) => ({
  title: issue.title,
  status: issue.status,
  priority: issue.priority,
  assignee: issue.assignee,
  dueDate: issue.dueDate || null,
  description: issue.description,
  tags: issue.tags,
});

const fetchIssuesFromApi = async () => {
  const data = await requestJson<ApiIssue[] | PaginatedResponse<ApiIssue>>(`${apiBaseUrl}/issues/`);
  const results = Array.isArray(data) ? data : data.results;
  return results.map(mapApiIssue);
};

const createIssueOnApi = async (issue: Issue) => {
  const data = await requestJson<ApiIssue>(`${apiBaseUrl}/issues/`, {
    method: "POST",
    body: JSON.stringify(getIssuePayload(issue)),
  });

  return mapApiIssue(data);
};

const updateIssueOnApi = async (issue: Issue) => {
  if (!issue.backendId) {
    throw new Error("서버 이슈 ID가 없어 수정할 수 없습니다.");
  }

  const data = await requestJson<ApiIssue>(`${apiBaseUrl}/issues/${issue.backendId}/`, {
    method: "PATCH",
    body: JSON.stringify(getIssuePayload(issue)),
  });

  return mapApiIssue(data);
};

const uploadIssueAttachments = async (issueId: number, files: File[]) => {
  if (!files.length) return [];

  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const data = await requestJson<ApiIssueAttachment[]>(`${apiBaseUrl}/issues/${issueId}/attachments/`, {
    method: "POST",
    body: formData,
  });

  return data.map(mapApiAttachment);
};

const isImageAttachment = (attachment: IssueAttachment) => {
  return (
    attachment.type.startsWith("image/") ||
    /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(attachment.name)
  );
};

const normalizeUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getNextIssueId = (issues: Issue[]) => {
  const maxNumber = issues.reduce((max, issue) => {
    const issueNumber = Number(issue.id.replace("NOS-", ""));
    return Number.isNaN(issueNumber) ? max : Math.max(max, issueNumber);
  }, 1000);

  return `NOS-${maxNumber + 1}`;
};

const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
};

const createEmptyIssue = (issues: Issue[]): Issue => ({
  id: getNextIssueId(issues),
  title: "",
  client: "고객사",
  status: "backlog",
  priority: "medium",
  assignee: "Frontend",
  dueDate: getDefaultDueDate(),
  tags: [],
  description:
    "<h2>요구사항</h2><p>새 이슈의 목적과 배경을 작성하세요.</p><ul><li>주요 작업 항목</li><li>확인이 필요한 조건</li><li>완료 기준</li></ul>",
  attachments: [],
});

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const markdownToEditorHtml = (value: string) => {
  if (value.trim().startsWith("<")) {
    return value;
  }

  const lines = value.split("\n");
  const htmlParts: string[] = [];
  let listOpen = false;

  lines.forEach((line) => {
    if (line.startsWith("- ")) {
      if (!listOpen) {
        htmlParts.push("<ul>");
        listOpen = true;
      }
      htmlParts.push(`<li>${escapeHtml(line.replace("- ", ""))}</li>`);
      return;
    }

    if (listOpen) {
      htmlParts.push("</ul>");
      listOpen = false;
    }

    if (line.startsWith("### ")) {
      htmlParts.push(`<h3>${escapeHtml(line.replace("### ", ""))}</h3>`);
      return;
    }

    if (line.startsWith("## ")) {
      htmlParts.push(`<h2>${escapeHtml(line.replace("## ", ""))}</h2>`);
      return;
    }

    if (line.startsWith("> ")) {
      htmlParts.push(
        `<blockquote>${escapeHtml(line.replace("> ", ""))}</blockquote>`,
      );
      return;
    }

    if (line.trim()) {
      htmlParts.push(`<p>${escapeHtml(line)}</p>`);
    }
  });

  if (listOpen) {
    htmlParts.push("</ul>");
  }

  return htmlParts.join("");
};

type EditorIconName =
  | "heading"
  | "paragraph"
  | "bold"
  | "italic"
  | "list"
  | "numberList"
  | "quote"
  | "code"
  | "link";

const EditorIcon: React.FC<{ name: EditorIconName }> = ({ name }) => {
  const paths: Record<EditorIconName, string> = {
    heading: "M4 5h2v6h8V5h2v14h-2v-6H6v6H4V5Zm13 12h3v2h-3v-2Zm0-4h3v2h-3v-2Z",
    paragraph:
      "M6 4h7a5 5 0 0 1 0 10h-1v6h-2V6H8v14H6V4Zm6 8h1a3 3 0 0 0 0-6h-1v6Z",
    bold: "M6 4h6.5A4.5 4.5 0 0 1 15.7 11 4.7 4.7 0 0 1 13 20H6V4Zm3 6h3.2a2 2 0 0 0 0-4H9v4Zm0 8h3.8a2.2 2.2 0 0 0 0-4.4H9V18Z",
    italic: "M10 4h8v2h-3.1l-3.8 12H14v2H6v-2h3.1l3.8-12H10V4Z",
    list: "M4 6.5A1.5 1.5 0 1 1 7 6.5a1.5 1.5 0 0 1-3 0ZM9 6h11v2H9V6Zm-5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm5-.5h11v2H9v-2Zm-5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM9 17h11v2H9v-2Z",
    numberList:
      "M4 5h2v5H5V7H4V5Zm0 8h3v2H5v1h2v2H4v-5Zm6-7h10v2H10V6Zm0 6h10v2H10v-2Zm0 6h10v2H10v-2Z",
    quote:
      "M5 5h6v7H8.2c.1 2 1.1 3.4 3 4.4L10 19c-3.4-1.6-5-4.2-5-8V5Zm9 0h6v7h-2.8c.1 2 1.1 3.4 3 4.4L19 19c-3.4-1.6-5-4.2-5-8V5Z",
    code: "m8.7 16.3-1.4 1.4L1.6 12l5.7-5.7 1.4 1.4L4.4 12l4.3 4.3Zm6.6 0 4.3-4.3-4.3-4.3 1.4-1.4 5.7 5.7-5.7 5.7-1.4-1.4ZM13.2 4l2 .5L10.8 20l-2-.5L13.2 4Z",
    link: "M10.6 13.4a1 1 0 0 1 0-1.4l3.4-3.4a3 3 0 0 1 4.2 4.2l-3.4 3.4a3 3 0 0 1-4.2 0l1.4-1.4a1 1 0 0 0 1.4 0l3.4-3.4A1 1 0 0 0 15.4 10L12 13.4a1 1 0 0 1-1.4 0Zm2.8-2.8a1 1 0 0 1 0 1.4L10 15.4a1 1 0 0 0 1.4 1.4l3.4-3.4a1 1 0 0 1 1.4 1.4l-3.4 3.4a3 3 0 0 1-4.2-4.2l3.4-3.4a1 1 0 0 1 1.4 0Z",
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={paths[name]} />
    </svg>
  );
};

const IssuesPage: React.FC = () => {
  const account = getStoredAccount();
  const clientName = account?.client.name ?? "고객사";
  const [issues, setIssues] = useState<Issue[]>([]);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [draftIssue, setDraftIssue] = useState<Issue | null>(null);
  const [draftContentHtml, setDraftContentHtml] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [viewerImage, setViewerImage] = useState<IssueAttachment | null>(null);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [savingIssue, setSavingIssue] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const objectUrlsRef = useRef<string[]>([]);

  const isCreateMode = selectedIssueId === null && draftIssue !== null;

  const loadIssues = useCallback(async () => {
    try {
      setLoadingIssues(true);
      setIssueError(null);
      setIssues(await fetchIssuesFromApi());
    } catch (err) {
      setIssueError(err instanceof Error ? err.message : "이슈 목록을 불러오지 못했습니다.");
      setIssues(initialIssues.map((issue) => ({ ...issue, client: clientName })));
    } finally {
      setLoadingIssues(false);
    }
  }, [clientName]);

  useEffect(() => {
    const objectUrls = objectUrlsRef.current;

    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const issueCounts = useMemo(() => {
    return columns.reduce<Record<IssueStatus, number>>(
      (acc, column) => {
        acc[column.id] = issues.filter(
          (issue) => issue.status === column.id,
        ).length;
        return acc;
      },
      { backlog: 0, progress: 0, testing: 0, done: 0 },
    );
  }, [issues]);

  const openIssue = (issue: Issue) => {
    setSelectedIssueId(issue.id);
    setDraftIssue({
      ...issue,
      attachments: [...issue.attachments],
      tags: [...issue.tags],
    });
    setDraftContentHtml(markdownToEditorHtml(issue.description));
  };

  const openCreateIssue = () => {
    const nextIssue = createEmptyIssue(issues);
    nextIssue.client = clientName;
    setSelectedIssueId(null);
    setDraftIssue(nextIssue);
    setDraftContentHtml(nextIssue.description);
  };

  const closeModal = () => {
    setSelectedIssueId(null);
    setDraftIssue(null);
    setDraftContentHtml("");
    setIsDragging(false);
  };

  const updateDraftIssue = (patch: Partial<Issue>) => {
    setDraftIssue((current) => (current ? { ...current, ...patch } : current));
  };

  const saveIssue = async () => {
    if (!draftIssue) return;

    const normalizedIssue: Issue = {
      ...draftIssue,
      title: draftIssue.title.trim() || "제목 없는 이슈",
      client: clientName,
      assignee: draftIssue.assignee.trim() || "Unassigned",
      tags: draftIssue.tags.map((tag) => tag.trim()).filter(Boolean),
      description: editorRef.current?.innerHTML || draftContentHtml,
    };

    const pendingFiles = normalizedIssue.attachments
      .map((attachment) => attachment.file)
      .filter((file): file is File => Boolean(file));

    try {
      setSavingIssue(true);
      setIssueError(null);

      const savedIssue = isCreateMode
        ? await createIssueOnApi(normalizedIssue)
        : await updateIssueOnApi(normalizedIssue);

      if (savedIssue.backendId && pendingFiles.length) {
        await uploadIssueAttachments(savedIssue.backendId, pendingFiles);
      }

      await loadIssues();
      closeModal();
    } catch (err) {
      setIssueError(err instanceof Error ? err.message : "이슈 저장에 실패했습니다.");
    } finally {
      setSavingIssue(false);
    }
  };

  const addAttachments = (fileList: FileList | null) => {
    if (!draftIssue || !fileList?.length) return;

    const newAttachments: IssueAttachment[] = Array.from(fileList).map(
      (file) => {
        const attachment: IssueAttachment = {
          id: `${draftIssue.id}-${file.name}-${file.lastModified}`,
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type || "unknown",
          file,
        };

        if (isImageAttachment(attachment)) {
          const previewUrl = URL.createObjectURL(file);
          objectUrlsRef.current.push(previewUrl);
          attachment.previewUrl = previewUrl;
        }

        return attachment;
      },
    );

    updateDraftIssue({
      attachments: [...draftIssue.attachments, ...newAttachments],
    });
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    addAttachments(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    addAttachments(event.dataTransfer.files);
  };

  const selectedAttachments = draftIssue?.attachments ?? [];

  const selectionBelongsToEditor = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current)
      return false;

    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;

    return Boolean(
      anchorNode &&
      focusNode &&
      editorRef.current.contains(anchorNode) &&
      editorRef.current.contains(focusNode),
    );
  };

  const placeCaretAtEnd = (element: HTMLElement) => {
    const range = document.createRange();
    const selection = window.getSelection();

    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const placeCaretAtStart = (element: HTMLElement) => {
    const range = document.createRange();
    const selection = window.getSelection();

    range.selectNodeContents(element);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const focusEditorSafely = () => {
    if (!editorRef.current) return;

    editorRef.current.focus();

    if (!selectionBelongsToEditor()) {
      placeCaretAtEnd(editorRef.current);
    }
  };

  const getCurrentEditorBlock = () => {
    const selection = window.getSelection();
    if (!selection?.anchorNode || !editorRef.current) return null;

    const baseNode =
      selection.anchorNode.nodeType === Node.TEXT_NODE
        ? selection.anchorNode.parentElement
        : selection.anchorNode;

    if (!(baseNode instanceof HTMLElement)) return null;

    return baseNode.closest("h2, h3, p, li, blockquote, pre");
  };

  const isSelectionInsideList = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) {
      return false;
    }

    const range = selection.getRangeAt(0);
    const startNode =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement
        : range.startContainer;
    const endNode =
      range.endContainer.nodeType === Node.TEXT_NODE
        ? range.endContainer.parentElement
        : range.endContainer;

    if (
      !(startNode instanceof HTMLElement) ||
      !(endNode instanceof HTMLElement)
    ) {
      return false;
    }

    return Boolean(startNode.closest("li") || endNode.closest("li"));
  };

  const runEditorToolbarAction = (
    event: React.MouseEvent<HTMLButtonElement>,
    action: () => void,
  ) => {
    event.preventDefault();
    focusEditorSafely();
    action();
  };

  const applyEditorCommand = (command: string, value?: string) => {
    focusEditorSafely();
    document.execCommand(command, false, value);
  };

  const applyHeadingFormat = () => {
    if (isSelectionInsideList()) {
      return;
    }

    applyEditorCommand("formatBlock", "h2");
  };

  const insertEditorHtml = (html: string) => {
    focusEditorSafely();
    document.execCommand("insertHTML", false, html);
  };

  const applyLinkFormat = () => {
    focusEditorSafely();

    const rawUrl = window.prompt("연결할 링크 주소를 입력하세요.", "https://");
    const href = normalizeUrl(rawUrl ?? "");
    if (!href) return;

    const selection = window.getSelection();

    if (
      selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      selectionBelongsToEditor()
    ) {
      document.execCommand("createLink", false, href);
      editorRef.current?.querySelectorAll("a").forEach((anchor) => {
        if (anchor.getAttribute("href") === href) {
          anchor.setAttribute("target", "_blank");
          anchor.setAttribute("rel", "noopener noreferrer");
        }
      });
      return;
    }

    insertEditorHtml(
      `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(href)}</a>&nbsp;`,
    );
  };

  const handleEditorClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const anchor = target.closest("a");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!href) return;

    event.preventDefault();
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleEditorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentBlock = getCurrentEditorBlock();

    if (event.key === "Tab" && currentBlock?.closest("li")) {
      event.preventDefault();
      document.execCommand(event.shiftKey ? "outdent" : "indent");
      return;
    }

    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      currentBlock instanceof HTMLElement &&
      ["H2", "H3"].includes(currentBlock.tagName)
    ) {
      event.preventDefault();

      const paragraph = document.createElement("p");
      paragraph.innerHTML = "<br>";
      currentBlock.after(paragraph);
      placeCaretAtStart(paragraph);
    }
  };

  return (
    <Page>
      <PageHeader>
        <HeaderCopy>
          <Eyebrow>Issue Tracking</Eyebrow>
          <Title>Kanban Operations Board</Title>
          <Description>
            고객사 요청, 개발 작업, QA 항목을 상태별로 추적합니다. 각 카드는
            상세 모달에서 마크다운 기반으로 내용을 정리하고 관련 파일을 첨부할
            수 있습니다.
          </Description>
        </HeaderCopy>
        <HeaderActions>
          <ActionButton type="button" onClick={openCreateIssue}>
            새 이슈 등록
          </ActionButton>
        </HeaderActions>
      </PageHeader>

      {loadingIssues && <ApiNotice>이슈 데이터를 서버에서 불러오는 중입니다.</ApiNotice>}
      {issueError && <ApiNotice $tone="danger">{issueError}</ApiNotice>}

      <SummaryGrid>
        {columns.map((column) => (
          <SummaryCard key={column.id}>
            <SummaryLabel>{column.title}</SummaryLabel>
            <SummaryValue>{issueCounts[column.id]}</SummaryValue>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <Board aria-label="이슈 칸반 보드">
        {columns.map((column) => {
          const columnIssues = issues.filter(
            (issue) => issue.status === column.id,
          );

          return (
            <ColumnPanel key={column.id}>
              <ColumnHeader>
                <ColumnTitleGroup>
                  <ColumnTitle>{column.title}</ColumnTitle>
                  <ColumnDescription>{column.description}</ColumnDescription>
                </ColumnTitleGroup>
                <CountBadge>{columnIssues.length}</CountBadge>
              </ColumnHeader>

              <CardStack>
                {columnIssues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    type="button"
                    onClick={() => openIssue(issue)}
                  >
                    <IssueMetaRow>
                      <IssueId>{issue.id}</IssueId>
                      <PriorityBadge $priority={issue.priority}>
                        {issue.priority}
                      </PriorityBadge>
                    </IssueMetaRow>
                    <IssueTitle>{issue.title}</IssueTitle>
                    <IssueInfo>
                      <InfoItem>
                        <span>Client</span>
                        <strong>{issue.client}</strong>
                      </InfoItem>
                      <InfoItem>
                        <span>Assignee</span>
                        <strong>{issue.assignee}</strong>
                      </InfoItem>
                      <InfoItem>
                        <span>Due</span>
                        <strong>{issue.dueDate}</strong>
                      </InfoItem>
                      <InfoItem>
                        <span>Files</span>
                        <strong>{issue.attachments.length}개</strong>
                      </InfoItem>
                    </IssueInfo>
                    <TagList>
                      {issue.tags.map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </TagList>
                  </IssueCard>
                ))}
              </CardStack>
            </ColumnPanel>
          );
        })}
      </Board>

      {draftIssue && (
        <ModalBackdrop role="presentation" onMouseDown={closeModal}>
          <ModalPanel
            role="dialog"
            aria-modal="true"
            aria-labelledby="issue-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitleGroup>
                <ModalKicker>
                  {isCreateMode ? "새 이슈 등록" : draftIssue.id}
                </ModalKicker>
                <ModalTitle id="issue-modal-title">
                  {isCreateMode ? "신규 이슈 작성" : draftIssue.title}
                </ModalTitle>
              </ModalTitleGroup>
              <CloseButton
                type="button"
                onClick={closeModal}
                aria-label="이슈 상세 닫기"
              >
                ×
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <EditorSection>
                <FormGrid>
                  <FieldLabel>
                    이슈 제목
                    <TextInput
                      value={draftIssue.title}
                      onChange={(event) =>
                        updateDraftIssue({ title: event.target.value })
                      }
                      placeholder="예: 관리자 리포트 Export 개선"
                    />
                  </FieldLabel>
                  <FieldLabel>
                    고객사
                    <TextInput
                      value={draftIssue.client}
                      readOnly
                      placeholder={clientName}
                    />
                  </FieldLabel>
                  <FieldLabel>
                    담당자
                    <TextInput
                      value={draftIssue.assignee}
                      onChange={(event) =>
                        updateDraftIssue({ assignee: event.target.value })
                      }
                      placeholder="Frontend"
                    />
                  </FieldLabel>
                  <FieldLabel>
                    마감일
                    <TextInput
                      type="date"
                      value={draftIssue.dueDate}
                      onChange={(event) =>
                        updateDraftIssue({ dueDate: event.target.value })
                      }
                    />
                  </FieldLabel>
                  <FieldLabel>
                    상태
                    <SelectInput
                      value={draftIssue.status}
                      onChange={(event) =>
                        updateDraftIssue({
                          status: event.target.value as IssueStatus,
                        })
                      }
                    >
                      {columns.map((column) => (
                        <option key={column.id} value={column.id}>
                          {column.title}
                        </option>
                      ))}
                    </SelectInput>
                  </FieldLabel>
                  <FieldLabel>
                    우선순위
                    <SelectInput
                      value={draftIssue.priority}
                      onChange={(event) =>
                        updateDraftIssue({
                          priority: event.target.value as IssuePriority,
                        })
                      }
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </SelectInput>
                  </FieldLabel>
                  <FieldLabel>
                    태그
                    <TextInput
                      value={draftIssue.tags.join(", ")}
                      onChange={(event) =>
                        updateDraftIssue({
                          tags: event.target.value
                            .split(",")
                            .map((tag) => tag.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="Billing, CSV, Admin"
                    />
                  </FieldLabel>
                </FormGrid>
                <FieldGroup>
                  이슈 본문
                  <RichEditor>
                    <EditorToolbar aria-label="본문 서식 도구">
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            applyHeadingFormat(),
                          )
                        }
                      >
                        <EditorIcon name="heading" />
                        제목
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            applyEditorCommand("formatBlock", "p"),
                          )
                        }
                      >
                        <EditorIcon name="paragraph" />
                        본문
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            applyEditorCommand("bold"),
                          )
                        }
                      >
                        <EditorIcon name="bold" />
                        굵게
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            applyEditorCommand("italic"),
                          )
                        }
                      >
                        <EditorIcon name="italic" />
                        기울임
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            applyEditorCommand("insertUnorderedList"),
                          )
                        }
                      >
                        <EditorIcon name="list" />
                        목록
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            applyEditorCommand("insertOrderedList"),
                          )
                        }
                      >
                        <EditorIcon name="numberList" />
                        번호
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            insertEditorHtml(
                              "<blockquote>강조할 내용을 입력하세요.</blockquote><p><br></p>",
                            ),
                          )
                        }
                      >
                        <EditorIcon name="quote" />
                        인용
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () =>
                            insertEditorHtml(
                              "<pre><code>코드 또는 로그를 입력하세요.</code></pre><p><br></p>",
                            ),
                          )
                        }
                      >
                        <EditorIcon name="code" />
                        코드
                      </ToolbarButton>
                      <ToolbarButton
                        type="button"
                        onMouseDown={(event) =>
                          runEditorToolbarAction(event, () => applyLinkFormat())
                        }
                      >
                        <EditorIcon name="link" />
                        링크
                      </ToolbarButton>
                    </EditorToolbar>
                    <EditorSurface
                      key={draftIssue.id}
                      ref={editorRef}
                      contentEditable
                      role="textbox"
                      aria-label="이슈 본문 편집기"
                      aria-multiline="true"
                      tabIndex={0}
                      spellCheck
                      suppressContentEditableWarning
                      onKeyDown={handleEditorKeyDown}
                      onClick={handleEditorClick}
                      dangerouslySetInnerHTML={{ __html: draftContentHtml }}
                    />
                  </RichEditor>
                </FieldGroup>
              </EditorSection>

              <SidePanel>
                <DetailGrid>
                  <DetailItem>
                    <span>Status</span>
                    <strong>
                      {
                        columns.find(
                          (column) => column.id === draftIssue.status,
                        )?.title
                      }
                    </strong>
                  </DetailItem>
                  <DetailItem>
                    <span>Priority</span>
                    <strong>{draftIssue.priority}</strong>
                  </DetailItem>
                  <DetailItem>
                    <span>Assignee</span>
                    <strong>{draftIssue.assignee}</strong>
                  </DetailItem>
                  <DetailItem>
                    <span>Due Date</span>
                    <strong>{draftIssue.dueDate}</strong>
                  </DetailItem>
                </DetailGrid>

                <DropZone
                  $dragging={isDragging}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <input type="file" multiple onChange={handleFileInput} />
                  <div>
                    <strong>파일 업로드</strong>
                    <span>
                      이미지는 첨부 후 별도 뷰어에서 바로 확인할 수 있습니다.
                    </span>
                  </div>
                </DropZone>

                <AttachmentList>
                  {selectedAttachments.map((attachment) => (
                    <AttachmentItem
                      key={attachment.id}
                      type="button"
                      $clickable={Boolean(attachment.previewUrl)}
                      onClick={() => {
                        if (attachment.previewUrl) {
                          setViewerImage(attachment);
                        }
                      }}
                    >
                      <strong>{attachment.name}</strong>
                      <span>
                        {attachment.previewUrl
                          ? "이미지 보기"
                          : attachment.size}
                      </span>
                    </AttachmentItem>
                  ))}
                </AttachmentList>
              </SidePanel>
            </ModalBody>

            <ModalFooter>
              <SecondaryButton type="button" onClick={closeModal}>
                취소
              </SecondaryButton>
              <PrimaryButton type="button" onClick={saveIssue} disabled={savingIssue}>
                {savingIssue ? "저장 중..." : isCreateMode ? "이슈 등록" : "변경사항 저장"}
              </PrimaryButton>
            </ModalFooter>
          </ModalPanel>
        </ModalBackdrop>
      )}

      {viewerImage?.previewUrl && (
        <ImageViewerBackdrop
          role="presentation"
          onMouseDown={() => setViewerImage(null)}
        >
          <ImageViewerPanel
            role="dialog"
            aria-modal="true"
            aria-label={`${viewerImage.name} 이미지 미리보기`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ImageViewerHeader>
              <strong>{viewerImage.name}</strong>
              <CloseButton
                type="button"
                onClick={() => setViewerImage(null)}
                aria-label="이미지 뷰어 닫기"
              >
                ×
              </CloseButton>
            </ImageViewerHeader>
            <ImageViewerBody>
              <img src={viewerImage.previewUrl} alt={viewerImage.name} />
            </ImageViewerBody>
          </ImageViewerPanel>
        </ImageViewerBackdrop>
      )}
    </Page>
  );
};

export default IssuesPage;
